/**
 * OrgStructure MX — Servicio de Inteligencia Estructural
 * v1.0.0
 * 
 * RESTRICCIÓN: Este servicio SOLO consulta datos internos de COPARMEX
 * (tablas cpx_* en Supabase). NO accede a fuentes externas.
 * 
 * Módulos fuente: Planeación (cpx_work_plans, cpx_work_plan_sections)
 *                 Seguimiento (cpx_indicators, cpx_monthly_reports)
 *                 Estructura (cpx_vicepresidencies, cpx_commissions)
 */
import { supabase } from './supabase';
import type { User, Commission, Vicepresidency, Indicator } from './supabase-service';
import {
  getHierarchy,
  getWorkPlans,
  getDashboardByCommission,
  getDashboardByVP,
  getIndicators,
  getTracking,
} from './supabase-service';

// ─── Tipos OrgStructure ─────────────────────────────────

/** Resultado de conteos organizacionales */
export interface OrgCounts {
  vicepresidencias: number;
  comisiones: number;
  comites: number;
  consejerias: number;
  total_instancias: number;
}

/** Detalle de una instancia con métricas */
export interface InstanceDetail {
  id: string;
  name: string;
  type: 'COMISION' | 'COMITE' | 'CONSEJERIA';
  vicepresidency_id: string | null;
  vp_name: string;
  president_name: string;
  plan_status: string;
  pct_cumplimiento: number;
  total_indicators: number;
  kpi_green: number;
  kpi_amber: number;
  kpi_red: number;
}

/** Resumen por Vicepresidencia */
export interface VPSummary {
  id: string;
  name: string;
  president_name: string;
  comisiones: number;
  comites: number;
  consejerias: number;
  total_instancias: number;
  pct_cumplimiento: number;
  approved_plans: number;
  pending_plans: number;
}

/** Insight Estratégico generado internamente */
export interface StrategicInsight {
  id: string;
  type: 'warning' | 'success' | 'info' | 'action';
  title: string;
  description: string;
  metric?: string;
  icon: string;
}

/** Resultado completo del análisis */
export interface OrgStructureResult {
  hasAccess: boolean;
  accessScope: 'GLOBAL' | 'COORDINATOR' | 'VP' | 'INSTANCE' | 'NONE';
  accessMessage: string;
  counts: OrgCounts;
  vpSummaries: VPSummary[];
  instances: InstanceDetail[];
  insights: StrategicInsight[];
  restrictedSections: string[];
  dataSource: 'PLANEACION_SEGUIMIENTO_COPARMEX';
  timestamp: string;
}

// ─── Helpers de Acceso ──────────────────────────────────

/**
 * Determina el ámbito organizacional del usuario autenticado.
 * Usa la misma lógica que el Hierarchy Shield existente.
 */
const resolveScope = (user: User): 'GLOBAL' | 'COORDINATOR' | 'VP' | 'INSTANCE' => {
  const scope = user.org_scope || (
    user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR'
      ? 'GLOBAL'
      : user.role === 'COORDINADOR'
        ? 'COORDINATOR'
        : user.role === 'VICE_PRESIDENT'
          ? 'VP'
          : 'INSTANCE'
  );
  return scope as 'GLOBAL' | 'COORDINATOR' | 'VP' | 'INSTANCE';
};

/**
 * Verifica si el usuario tiene acceso a los módulos de Planeación y Seguimiento.
 * Esta es la verificación OBLIGATORIA antes de mostrar cualquier dato.
 */
export const checkModuleAccess = (user: User): { hasPlanificacion: boolean; hasSeguimiento: boolean } => {
  const modules = user.accessible_modules || [];
  const role = user.role;

  // SUPER_ADMIN y ADMINISTRADOR siempre tienen acceso total
  if (role === 'SUPER_ADMIN' || role === 'ADMINISTRADOR') {
    return { hasPlanificacion: true, hasSeguimiento: true };
  }

  // Para otros roles, verificar módulos asignados
  const moduleNames = modules.map(m => {
    if (m.includes(':')) return m.split(':')[0];
    return m;
  });

  // Si no tiene módulos asignados, verificar por rol default
  if (moduleNames.length === 0) {
    // Roles por default tienen acceso como VISOR a todos
    return { hasPlanificacion: true, hasSeguimiento: true };
  }

  return {
    hasPlanificacion: moduleNames.includes('Planeación'),
    hasSeguimiento: moduleNames.includes('Seguimiento'),
  };
};

// ─── Motor Principal ────────────────────────────────────

/**
 * Obtiene el análisis estructural completo de la organización COPARMEX.
 * SOLO usa datos internos de los módulos de Planeación y Seguimiento.
 * 
 * @param {User} user - Usuario autenticado con su ámbito de acceso.
 * @param {string} [searchQuery] - Filtro opcional por nombre de instancia.
 * @returns {Promise<OrgStructureResult>} Análisis completo filtrado por acceso.
 */
export const getOrgStructureAnalysis = async (
  user: User,
  searchQuery?: string
): Promise<OrgStructureResult> => {
  const timestamp = new Date().toISOString();
  const scope = resolveScope(user);
  const { hasPlanificacion, hasSeguimiento } = checkModuleAccess(user);

  // ── GUARD: Verificar acceso a módulos ──
  if (!hasPlanificacion && !hasSeguimiento) {
    return {
      hasAccess: false,
      accessScope: 'NONE',
      accessMessage: 'No tengo datos disponibles dentro de tu ámbito de acceso actual.',
      counts: { vicepresidencias: 0, comisiones: 0, comites: 0, consejerias: 0, total_instancias: 0 },
      vpSummaries: [],
      instances: [],
      insights: [],
      restrictedSections: ['Planeación', 'Seguimiento'],
      dataSource: 'PLANEACION_SEGUIMIENTO_COPARMEX',
      timestamp,
    };
  }

  const restrictedSections: string[] = [];
  if (!hasPlanificacion) restrictedSections.push('Planeación');
  if (!hasSeguimiento) restrictedSections.push('Seguimiento');

  try {
    // ── Paso 1: Obtener estructura jerárquica según acceso del usuario ──
    const hierarchy = await getHierarchy(user);
    const vps = hierarchy.vicepresidencies || [];
    const allComms = [...(hierarchy.commissions || []), ...(hierarchy.independents || [])];

    // Filtrar por búsqueda si existe
    let filteredComms = allComms;
    if (searchQuery && searchQuery.trim()) {
      const normalize = (s: string) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
      const query = normalize(searchQuery);
      filteredComms = allComms.filter(c => normalize(c.name).includes(query));
    }

    // ── Paso 2: Clasificar instancias por tipo ──
    const comisiones = filteredComms.filter(c => c.type === 'COMISION');
    const comites = filteredComms.filter(c => c.type === 'COMITE');
    const consejerias = filteredComms.filter(c => c.type === 'CONSEJERIA');

    const counts: OrgCounts = {
      vicepresidencias: vps.length,
      comisiones: comisiones.length,
      comites: comites.length,
      consejerias: consejerias.length,
      total_instancias: filteredComms.length,
    };

    // ── Paso 3: Obtener métricas de Planeación (si tiene acceso) ──
    let plans: any[] = [];
    if (hasPlanificacion) {
      plans = await getWorkPlans(user);
    }

    // ── Paso 4: Obtener métricas de Dashboard (indicadores/KPI) ──
    let dashboardData: any[] = [];
    if (hasSeguimiento) {
      try {
        dashboardData = await getDashboardByCommission(user);
      } catch (err) {
        console.warn('[OrgStructure] Error obteniendo dashboard:', err);
      }
    }

    // ── Paso 5: Construir detalle por instancia ──
    const vpMap = new Map(vps.map(v => [v.id, v.name]));
    const dashMap = new Map(dashboardData.map(d => [d.commission_id, d]));
    const plansByComm = new Map<string, any[]>();
    plans.forEach(p => {
      const existing = plansByComm.get(p.commission_id) || [];
      existing.push(p);
      plansByComm.set(p.commission_id, existing);
    });

    const instances: InstanceDetail[] = filteredComms.map(c => {
      const dash = dashMap.get(c.id);
      const commPlans = plansByComm.get(c.id) || [];
      const latestPlan = commPlans.sort((a, b) => b.year - a.year)[0];
      
      return {
        id: c.id,
        name: c.name,
        type: c.type as 'COMISION' | 'COMITE' | 'CONSEJERIA',
        vicepresidency_id: c.vicepresidency_id || null,
        vp_name: c.vicepresidency_id ? (vpMap.get(c.vicepresidency_id) || 'Sin VP') : 'Independiente',
        president_name: c.president_name || 'Sin asignar',
        plan_status: latestPlan?.status || (hasPlanificacion ? 'SIN PLAN' : '🔒 Restringido'),
        pct_cumplimiento: dash?.pct_cumplimiento || 0,
        total_indicators: 0, // Se llenará después si tiene acceso
        kpi_green: dash?.kpi_green || 0,
        kpi_amber: dash?.kpi_amber || 0,
        kpi_red: dash?.kpi_red || 0,
      };
    });

    // ── Paso 6: Obtener conteo de indicadores por instancia (si tiene acceso a Seguimiento) ──
    if (hasSeguimiento) {
      // Solo consultamos indicadores para las primeras 20 instancias para evitar sobrecarga
      const topInstances = instances.slice(0, 20);
      await Promise.all(
        topInstances.map(async (inst) => {
          const indicators = await getIndicators(inst.id);
          const idx = instances.findIndex(i => i.id === inst.id);
          if (idx !== -1) {
            instances[idx].total_indicators = indicators.length;
          }
        })
      );
    }

    // ── Paso 7: Construir resumen por VP ──
    const vpSummaries: VPSummary[] = vps.map(vp => {
      const vpComms = filteredComms.filter(c => c.vicepresidency_id === vp.id);
      const vpPlans = plans.filter(p => {
        const comm = filteredComms.find(c => c.id === p.commission_id);
        return comm?.vicepresidency_id === vp.id;
      });
      const vpDash = dashboardData.filter(d => d.vicepresidency_id === vp.id);
      
      const avgPct = vpDash.length > 0
        ? Math.round(vpDash.reduce((acc, d) => acc + (d.pct_cumplimiento || 0), 0) / vpDash.length)
        : 0;

      return {
        id: vp.id,
        name: vp.name,
        president_name: vp.president_name || 'Sin asignar',
        comisiones: vpComms.filter(c => c.type === 'COMISION').length,
        comites: vpComms.filter(c => c.type === 'COMITE').length,
        consejerias: vpComms.filter(c => c.type === 'CONSEJERIA').length,
        total_instancias: vpComms.length,
        pct_cumplimiento: avgPct,
        approved_plans: vpPlans.filter(p => p.status === 'APROBADO').length,
        pending_plans: vpPlans.filter(p => ['BORRADOR', 'EN REVISIÓN', 'REVISADO'].includes(p.status)).length,
      };
    });

    // ── Paso 8: Generar insights estratégicos (SOLO datos internos) ──
    const insights = generateInsights(counts, vpSummaries, instances, plans, hasPlanificacion, hasSeguimiento);

    return {
      hasAccess: true,
      accessScope: scope,
      accessMessage: scope === 'GLOBAL'
        ? 'Acceso completo — Visualizando toda la organización COPARMEX.'
        : scope === 'VP'
          ? `Acceso VP — Visualizando instancias de tu Vicepresidencia.`
          : scope === 'COORDINATOR'
            ? 'Acceso Coordinador — Visualizando instancias autorizadas.'
            : 'Acceso de instancia — Visualizando tu Comisión/Comité asignado.',
      counts,
      vpSummaries,
      instances,
      insights,
      restrictedSections,
      dataSource: 'PLANEACION_SEGUIMIENTO_COPARMEX',
      timestamp,
    };
  } catch (err) {
    console.error('[OrgStructure] Error crítico:', err);
    throw err;
  }
};

// ─── Motor de Insights ──────────────────────────────────

/**
 * Genera insights estratégicos EXCLUSIVAMENTE a partir de datos internos.
 * Nunca inventa ni estima fuera de los módulos de COPARMEX.
 */
const generateInsights = (
  counts: OrgCounts,
  vpSummaries: VPSummary[],
  instances: InstanceDetail[],
  plans: any[],
  hasPlanificacion: boolean,
  hasSeguimiento: boolean
): StrategicInsight[] => {
  const insights: StrategicInsight[] = [];
  let insightId = 0;

  // 1. Análisis de distribución por tipo
  if (counts.total_instancias > 0) {
    const pctComisiones = Math.round((counts.comisiones / counts.total_instancias) * 100);
    const pctComites = Math.round((counts.comites / counts.total_instancias) * 100);
    const pctConsejerias = Math.round((counts.consejerias / counts.total_instancias) * 100);

    insights.push({
      id: `insight-${++insightId}`,
      type: 'info',
      title: 'Distribución Organizacional',
      description: `La estructura cuenta con ${pctComisiones}% comisiones, ${pctComites}% comités y ${pctConsejerias}% consejerías. ${
        pctComisiones > 60 ? 'La concentración en comisiones sugiere una estructura operativa madura.' :
        pctComites > 40 ? 'El alto porcentaje de comités indica énfasis en trabajo técnico especializado.' :
        'Distribución equilibrada entre tipos de instancia.'
      }`,
      metric: `${counts.comisiones}C / ${counts.comites}K / ${counts.consejerias}J`,
      icon: 'PieChart',
    });
  }

  // 2. Análisis de planes de trabajo
  if (hasPlanificacion && plans.length > 0) {
    const approved = plans.filter(p => p.status === 'APROBADO').length;
    const drafts = plans.filter(p => p.status === 'BORRADOR').length;
    const inReview = plans.filter(p => ['EN REVISIÓN', 'REVISADO'].includes(p.status)).length;
    const pctApproved = Math.round((approved / plans.length) * 100);

    if (pctApproved < 50) {
      insights.push({
        id: `insight-${++insightId}`,
        type: 'warning',
        title: 'Planes Pendientes de Aprobación',
        description: `Solo ${pctApproved}% de los planes están aprobados. ${drafts} en borrador y ${inReview} en revisión. Acción recomendada: agilizar el proceso de revisión y aprobación.`,
        metric: `${approved}/${plans.length} aprobados`,
        icon: 'AlertTriangle',
      });
    } else {
      insights.push({
        id: `insight-${++insightId}`,
        type: 'success',
        title: 'Buena Cobertura de Planeación',
        description: `${pctApproved}% de los planes están aprobados. La organización mantiene un buen ritmo de planeación formal.`,
        metric: `${approved}/${plans.length} aprobados`,
        icon: 'CheckCircle2',
      });
    }
  }

  // 3. Análisis de cumplimiento por VP
  if (vpSummaries.length > 0) {
    const bestVP = vpSummaries.reduce((a, b) => a.pct_cumplimiento > b.pct_cumplimiento ? a : b);
    const worstVP = vpSummaries.reduce((a, b) => a.pct_cumplimiento < b.pct_cumplimiento ? a : b);
    const gap = bestVP.pct_cumplimiento - worstVP.pct_cumplimiento;

    if (gap > 30 && vpSummaries.length > 1) {
      insights.push({
        id: `insight-${++insightId}`,
        type: 'action',
        title: 'Brecha de Cumplimiento entre VPs',
        description: `La brecha entre "${bestVP.name}" (${bestVP.pct_cumplimiento}%) y "${worstVP.name}" (${worstVP.pct_cumplimiento}%) es de ${gap} puntos porcentuales. Acción: transferir mejores prácticas de la VP líder.`,
        metric: `Δ ${gap}%`,
        icon: 'TrendingUp',
      });
    }
  }

  // 4. Instancias sin indicadores
  if (hasSeguimiento) {
    const sinIndicadores = instances.filter(i => i.total_indicators === 0);
    if (sinIndicadores.length > 0) {
      insights.push({
        id: `insight-${++insightId}`,
        type: 'warning',
        title: 'Instancias sin Indicadores',
        description: `${sinIndicadores.length} instancia(s) no tienen indicadores de seguimiento definidos: ${sinIndicadores.slice(0, 3).map(i => i.name).join(', ')}${sinIndicadores.length > 3 ? ` y ${sinIndicadores.length - 3} más` : ''}. Acción: asignar indicadores clave de desempeño.`,
        metric: `${sinIndicadores.length} sin KPIs`,
        icon: 'AlertCircle',
      });
    }
  }

  // 5. Instancias sin plan
  if (hasPlanificacion) {
    const sinPlan = instances.filter(i => i.plan_status === 'SIN PLAN');
    if (sinPlan.length > 0) {
      insights.push({
        id: `insight-${++insightId}`,
        type: 'action',
        title: 'Instancias sin Plan de Trabajo',
        description: `${sinPlan.length} instancia(s) no cuentan con plan de trabajo vigente. Acción recomendada: crear planes de trabajo para el período actual.`,
        metric: `${sinPlan.length} sin plan`,
        icon: 'FileText',
      });
    }
  }

  // 6. KPIs en rojo
  const totalRed = instances.reduce((acc, i) => acc + i.kpi_red, 0);
  const totalGreen = instances.reduce((acc, i) => acc + i.kpi_green, 0);
  const totalAmber = instances.reduce((acc, i) => acc + i.kpi_amber, 0);
  const totalKPIs = totalRed + totalGreen + totalAmber;

  if (totalKPIs > 0 && totalRed > 0) {
    const pctRed = Math.round((totalRed / totalKPIs) * 100);
    insights.push({
      id: `insight-${++insightId}`,
      type: pctRed > 40 ? 'warning' : 'info',
      title: 'Semáforo de KPIs',
      description: `${pctRed}% de los indicadores están en rojo (${totalRed} de ${totalKPIs}). ${
        pctRed > 40 ? 'Se requiere intervención urgente en las instancias más rezagadas.' : 'Mantener monitoreo continuo.'
      }`,
      metric: `🟢${totalGreen} 🟡${totalAmber} 🔴${totalRed}`,
      icon: 'Activity',
    });
  }

  // 7. Ratio VP/Instancias (verticalidad)
  if (counts.vicepresidencias > 0 && counts.total_instancias > 0) {
    const ratio = (counts.total_instancias / counts.vicepresidencias).toFixed(1);
    insights.push({
      id: `insight-${++insightId}`,
      type: 'info',
      title: 'Índice de Verticalidad',
      description: `Cada vicepresidencia supervisa en promedio ${ratio} instancias. ${
        parseFloat(ratio) > 10 ? 'Alta densidad — considerar crear vicepresidencias adicionales para mejor supervisión.' :
        parseFloat(ratio) < 3 ? 'Baja densidad — posibilidad de consolidar vicepresidencias para mayor eficiencia.' :
        'Ratio equilibrado de supervisión.'
      }`,
      metric: `${ratio} inst/VP`,
      icon: 'Layers',
    });
  }

  return insights;
};
